/**
 * Plans API
 * Handles all API calls for the Plans feature
 */

import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const plansApi = {
  /**
   * Create a new plan
   */
  create: async (name, assetId) => {
    const response = await axios.post(`${API}/api/plans`, {
      name,
      asset_id: assetId
    });
    return response.data;
  },

  /**
   * Get all plans for the current user
   */
  getAll: async () => {
    const response = await axios.get(`${API}/api/plans`);
    return response.data;
  },

  /**
   * Get a single plan by ID
   */
  getById: async (planId) => {
    const response = await axios.get(`${API}/api/plans/${planId}`);
    return response.data;
  },

  /**
   * Update a plan (name or plan_state)
   */
  update: async (planId, data) => {
    const response = await axios.patch(`${API}/api/plans/${planId}`, data);
    return response.data;
  },

  /**
   * Delete a plan
   */
  delete: async (planId) => {
    const response = await axios.delete(`${API}/api/plans/${planId}`);
    return response.data;
  },

  /**
   * Update Step 0 (Goal & Offer) in plan_state
   */
  updateStep0: async (planId, stepData) => {
    // Get current plan to merge state
    const plan = await plansApi.getById(planId);
    const currentState = plan.plan_state || { steps: [], custom_options: {} };
    
    // Find or create step0
    const steps = [...(currentState.steps || [])];
    const step0Index = steps.findIndex(s => s.id === 'step0_goal_offer');
    
    if (step0Index >= 0) {
      steps[step0Index] = {
        ...steps[step0Index],
        status: 'completed',
        data: stepData
      };
    } else {
      steps.push({
        id: 'step0_goal_offer',
        status: 'completed',
        data: stepData
      });
    }
    
    const newState = {
      ...currentState,
      steps
    };
    
    return plansApi.update(planId, { plan_state: newState });
  },

  /**
   * Add a custom option (goal or offer) to the plan
   */
  addCustomOption: async (planId, type, value) => {
    const plan = await plansApi.getById(planId);
    const currentState = plan.plan_state || { steps: [], custom_options: {} };
    
    const customOptions = { ...currentState.custom_options };
    const key = type === 'goal' ? 'primary_goals' : 'offer_styles';
    
    if (!customOptions[key]) {
      customOptions[key] = [];
    }
    
    if (!customOptions[key].includes(value)) {
      customOptions[key].push(value);
    }
    
    const newState = {
      ...currentState,
      custom_options: customOptions
    };
    
    return plansApi.update(planId, { plan_state: newState });
  },

  // ==================== STEP 1: PERSONAS ====================

  /**
   * Get persona suggestions for a plan (saved in DB after first generation)
   * @param {string} planId - Plan ID
   * @param {boolean} regenerate - Force new generation (default: false)
   */
  getPersonaSuggestions: async (planId, regenerate = false) => {
    const response = await axios.get(`${API}/api/plans/${planId}/personas/suggestions`, {
      params: { regenerate }
    });
    return response.data;
  },

  /**
   * Build a persona from free-text (legacy)
   */
  buildPersona: async (planId, freeText) => {
    const response = await axios.post(`${API}/api/plans/${planId}/personas/build`, {
      free_text: freeText
    });
    return response.data;
  },

  /**
   * Add a new strategic persona to the plan.
   * Runs Agent A (Builder) + Agent B (QA/Exclusivity Guard).
   * Returns: { status: 'ok'|'overlap'|'error'|'invalid', persona?, message?, ... }
   */
  addPersona: async (planId, freeText) => {
    const response = await axios.post(`${API}/api/plans/${planId}/personas/add`, {
      free_text: freeText
    });
    return response.data;
  },

  /**
   * Force-add a persona bypassing overlap check.
   * Automatically downgrades confidence by one level.
   */
  addPersonaForce: async (planId, persona) => {
    const response = await axios.post(`${API}/api/plans/${planId}/personas/add-force`, {
      persona
    });
    return response.data;
  },

  /**
   * Save Step 1 personas
   */
  saveStep1: async (planId, selectedPersonaIds, planPersonas) => {
    const response = await axios.post(`${API}/api/plans/${planId}/step1`, {
      selected_persona_ids: selectedPersonaIds,
      plan_personas: planPersonas
    });
    return response.data;
  },

  // ========================================
  // Step 2: Segments
  // ========================================

  /**
   * Get or generate segments for selected personas
   * @param {string} planId - Plan ID
   * @param {boolean} regenerate - Force regeneration if true
   * @param {string} planFocus - Plan focus: acquire, convert, retarget, retain
   * @param {string} primaryOffer - Primary product/offer to focus on
   */
  getSegments: async (planId, regenerate = false, planFocus = null, primaryOffer = null) => {
    const params = { regenerate };
    if (planFocus) params.plan_focus = planFocus;
    if (primaryOffer) params.primary_offer = primaryOffer;
    
    const response = await axios.get(`${API}/api/plans/${planId}/segments/generate`, {
      params
    });
    return response.data;
  },

  /**
   * Save Step 2 segment selections
   */
  saveStep2: async (planId, generatedSegments, selectedSegmentIds) => {
    const response = await axios.post(`${API}/api/plans/${planId}/step2`, {
      generated_segments: generatedSegments,
      selected_segment_ids: selectedSegmentIds
    });
    return response.data;
  },

  /**
   * Add a custom segment from user free-text
   * @param {string} planId - Plan ID
   * @param {string} userHint - Free text describing the segment idea
   */
  addCustomSegment: async (planId, userHint) => {
    const response = await axios.post(`${API}/api/plans/${planId}/segments/add`, {
      user_hint: userHint
    });
    return response.data;
  },

  /**
   * Force-add a segment that was flagged as overlap
   * @param {string} planId - Plan ID
   * @param {object} segment - The segment to force-add
   */
  addSegmentForce: async (planId, segment) => {
    const response = await axios.post(`${API}/api/plans/${planId}/segments/add-force`, {
      segment
    });
    return response.data;
  },

  // ========================================
  // Step 3: Campaigns
  // ========================================

  /**
   * Generate campaign ideas for selected segments
   * @param {string} planId - Plan ID
   * @param {object} userControls - User control settings
   * @param {array} alreadyGeneratedIds - IDs of already generated campaigns (for "generate more")
   */
  generateCampaigns: async (planId, userControls, alreadyGeneratedIds = []) => {
    const response = await axios.post(`${API}/api/plans/${planId}/campaigns/generate`, {
      ...userControls,
      already_generated_campaign_ids: alreadyGeneratedIds
    });
    return response.data;
  },

  /**
   * Get saved campaigns for a plan
   * @param {string} planId - Plan ID
   */
  getCampaigns: async (planId) => {
    const response = await axios.get(`${API}/api/plans/${planId}/campaigns`);
    return response.data;
  },

  /**
   * Save Step 3 campaign selections
   * @param {string} planId - Plan ID
   * @param {array} generatedCampaigns - All generated campaigns
   * @param {array} selectedCampaignIds - IDs of selected campaigns
   * @param {object} userControls - User control settings used
   */
  saveStep3: async (planId, generatedCampaigns, selectedCampaignIds, userControls = {}) => {
    const response = await axios.post(`${API}/api/plans/${planId}/step3`, {
      generated_campaigns: generatedCampaigns,
      selected_campaign_ids: selectedCampaignIds,
      user_controls: userControls
    });
    return response.data;
  },

  /**
   * Clear Step 3 campaigns (called when earlier steps change)
   * @param {string} planId - Plan ID
   */
  clearStep3: async (planId) => {
    const response = await axios.post(`${API}/api/plans/${planId}/step3/clear`);
    return response.data;
  },

  // ========================================
  // Step 2.5: Qualifiers (Optional)
  // ========================================

  /**
   * Generate qualifiers (targeting profile) for a selected segment
   * @param {string} planId - Plan ID
   * @param {object} persona - Selected persona
   * @param {object} segment - Selected segment
   * @param {string} userHints - Optional user hints
   */
  generateQualifiers: async (planId, persona, segment, userHints = null) => {
    const response = await axios.post(`${API}/api/plans/${planId}/qualifiers/generate`, {
      persona,
      segment,
      user_hints: userHints
    });
    return response.data;
  },

  /**
   * Save user-selected qualifiers
   * @param {string} planId - Plan ID
   * @param {object} qualifiersSelected - User-selected qualifier hints
   */
  saveQualifiers: async (planId, qualifiersSelected) => {
    const response = await axios.post(`${API}/api/plans/${planId}/qualifiers/save`, {
      qualifiers_selected: qualifiersSelected
    });
    return response.data;
  },

  /**
   * Skip qualifiers step
   * @param {string} planId - Plan ID
   */
  skipQualifiers: async (planId) => {
    const response = await axios.post(`${API}/api/plans/${planId}/qualifiers/skip`);
    return response.data;
  },

  /**
   * Get saved qualifiers for a plan
   * @param {string} planId - Plan ID
   */
  getQualifiers: async (planId) => {
    const response = await axios.get(`${API}/api/plans/${planId}/qualifiers`);
    return response.data;
  }
};

export default plansApi;
